// src/PartnerAddOrder.jsx
import React, { useEffect, useRef, useState } from "react";
import { storage } from "./firebase";
import { apiFetch, API_BASE } from "./utils/apiClient";
import { generateInvoiceAssets, downloadInvoicePdf } from "./utils/generateInvoiceAssets";
import {
  getFramedPriceOptions,
  getCanvasPriceOptions,
  DEFAULT_CANVAS_UNIT_PRICE,
} from "./utils/boardPriceCalc";
import {
  FRAME_COLORS,
  PARTNER_FRAME_TYPES,
  PARTNER_SIZE_OPTIONS,
  PANEL_SHAPES,
  formatFrameColor,
} from "./utils/frameOptions";
import {
  Box,
  Typography,
  Button,
  Alert,
  MenuItem,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { PartnerShell, PageHeader, Card, Field, Suffix, Money } from "./partner/PartnerUI";
import { P } from "./partner/partnerTheme";

const IRAQ_GOVERNORATES = [
  "بغداد","نينوى","البصرة","أربيل","السليمانية","دهوك","كركوك","الأنبار",
  "بابل","كربلاء","النجف","القادسية","واسط","ميسان","ذي قار","المثنى",
  "صلاح الدين","ديالى","حلبجة"
];

const BOARD_ORDINALS = [
  "الأولى", "الثانية", "الثالثة", "الرابعة", "الخامسة",
  "السادسة", "السابعة", "الثامنة", "التاسعة", "العاشرة",
];
const boardTitle = (index) =>
  `قياس اللوحة ${BOARD_ORDINALS[index] || index + 1}`;

const digitsOnly = (v) => String(v).replace(/[^\d]/g, "");
const formatAmount = (n) => (n ? Number(n).toLocaleString("en-US") : "");
const parseAmount = (v) => Number(digitsOnly(v)) || 0;

const OTHER = "أخرى";

// نوع اللوحة بالفاتورة - نفس PARTNER_PANEL_TYPES بالباك اند (invoiceController.js)
const INVOICE_PANEL_TYPES = { origin: "أوريجن", edition: "إدشن" };

const safePdfName = (customerName) => {
  const cleaned = String(customerName || "").trim().replace(/[\\/:*?"<>|]/g, "-");
  return `فاتورة ${cleaned || "أرتيكا"}.pdf`;
};
const CUSTOM_SIZE = "custom";

// خيارات الإطار: مع إطار خارجي، أو بدون (فقط إطار داخلي خشب)
const FRAME_OPTION_CHOICES = [
  { value: "full", label: "مع إطار داخلي خشب وإطار خارجي" },
  { value: "inner", label: "بدون إطار - فقط مع إطار داخلي خشب" },
];

// القياسات حسب شكل اللوحة - نفس فلترة الفاتورة (مربعة ← المتساوية، مستطيلة ← غيرها)
const sizesForShape = (shape) => {
  if (!shape) return PARTNER_SIZE_OPTIONS;
  const square = shape === "لوحة مربعة";
  return PARTNER_SIZE_OPTIONS.filter((s) => {
    const [a, b] = s.split("x").map(Number);
    return square ? a === b : a !== b;
  });
};

// قياس من القائمة ← طول/عرض حسب الشكل: أفقية العرض هو الأكبر، عمودية الطول هو الأكبر
const dimsFromSize = (sizeKey, shape) => {
  const [a, b] = sizeKey.split("x").map(Number);
  const small = Math.min(a, b);
  const big = Math.max(a, b);
  return shape === "لوحة مستطيلة أفقية"
    ? { lengthCm: String(small), widthCm: String(big) }
    : { lengthCm: String(big), widthCm: String(small) };
};

let boardKeySeq = 0;
const newBoard = () => ({
  key: ++boardKeySeq,
  panelShape: "",
  sizeKey: "", // قياس من القائمة ("30x40") أو "custom" (أخرى - قياس يدوي)
  lengthCm: "",
  widthCm: "",
  quantity: "1",
  frameOption: "", // "full" (إطار داخلي + خارجي) | "inner" (بدون إطار - فقط داخلي خشب)
  frameType: "", // "L" (مودرن) | "Kas" (كلاسك) - مع الإطار الخارجي فقط
  frameColor: "", // طلب جديد فقط - أو "أخرى" + customFrameColor
  customFrameColor: "",
  // فئة سعر اللوحة: "origin" | "edition"
  priceOptionId: "",
  price: "", // نص منسَّق بالفواصل - ينحسب تلقائياً ويكدر يعدله
  images: [], // [{ file, previewUrl }]
});

// mode: "direct" (بيع مباشر - طلب مكتمل فوراً) | "order" (طلب جديد - يروح
// فاتورة غير معتمدة لواجهة الفواتير، وبعد اعتمادها يصير طلب). الصفحة بيها
// اختيار (radio) بين الاثنين، والـmode يحدد الاختيار الأولي فقط.
const PartnerAddOrder = ({ mode: initialMode = "direct" }) => {
  const PARTNER_ID = "art-touch";
  const [mode, setMode] = useState(initialMode);
  const isOrderMode = mode === "order";

  const [customer, setCustomer] = useState({
    customerName: "",
    customerPhone: "",
    governorate: "",
    nearestPoint: "",
  });
  const [notes, setNotes] = useState("");
  const [boards, setBoards] = useState(() => [newBoard()]);
  const [discountPercent, setDiscountPercent] = useState("");
  const [unitPrice, setUnitPrice] = useState(DEFAULT_CANVAS_UNIT_PRICE);
  const [savingOrder, setSavingOrder] = useState(false);
  const [savingStep, setSavingStep] = useState(""); // "pdf" | "save" - لنص زر الحفظ

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // سعر الطباعة للمتر (كانفاس) المعتمد من الأدمن - نفس مصدر واجهة حساب السعر
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE}/board-pricing`);
        if (!res.ok) return;
        const data = await res.json();
        if (Number(data.canvas) > 0) setUnitPrice(Number(data.canvas));
      } catch (err) {
        console.error("Failed to fetch board pricing:", err);
      }
    })();
  }, []);

  // تنظيف روابط معاينة الصور عند الخروج من الصفحة
  const boardsRef = useRef(boards);
  boardsRef.current = boards;
  useEffect(
    () => () =>
      boardsRef.current.forEach((b) =>
        b.images.forEach((img) => URL.revokeObjectURL(img.previewUrl))
      ),
    []
  );

  // أسعار اللوحة بفئتي Origin/Edition - مطابقة لحساب سعر اللوحة:
  // بدون إطار خارجي ← سعر الكانفاس (طباعة + إطار داخلي خشب)
  // مع إطار خارجي ← جداول/معادلة مودرن (أيل) أو كلاسك (كاس)
  const priceOptionsFor = (b) => {
    if (b.frameOption === "inner") return getCanvasPriceOptions(b.lengthCm, b.widthCm, unitPrice);
    if (b.frameOption === "full") return getFramedPriceOptions(b.lengthCm, b.widthCm, unitPrice, b.frameType);
    return [];
  };

  const hasDims = (b) => Number(b.lengthCm) > 0 && Number(b.widthCm) > 0;

  // المبلغ = سعر النوع المختار × العدد - ينعاد حسابه كلما تغيّر القياس/العدد/الإطار/النوع
  const withAutoPrice = (b) => {
    const option = priceOptionsFor(b).find((o) => o.id === b.priceOptionId);
    const qty = Number(b.quantity) || 0;
    return {
      ...b,
      price: option && qty > 0 ? formatAmount(option.price * qty) : "",
    };
  };

  const patchBoard = (key, fn) =>
    setBoards((prev) => prev.map((b) => (b.key === key ? withAutoPrice(fn(b)) : b)));

  // شكل اللوحة: يفلتر قائمة القياسات - إذا القياس المختار ما يناسب الشكل الجديد ينمسح
  const updateBoardShape = (key, shape) =>
    patchBoard(key, (b) => {
      if (b.sizeKey && b.sizeKey !== CUSTOM_SIZE) {
        if (!sizesForShape(shape).includes(b.sizeKey)) {
          return { ...b, panelShape: shape, sizeKey: "", lengthCm: "", widthCm: "" };
        }
        return { ...b, panelShape: shape, ...dimsFromSize(b.sizeKey, shape) };
      }
      return { ...b, panelShape: shape };
    });

  const updateBoardSize = (key, sizeKey) =>
    patchBoard(key, (b) =>
      sizeKey === CUSTOM_SIZE
        ? { ...b, sizeKey, lengthCm: "", widthCm: "" }
        : { ...b, sizeKey, ...dimsFromSize(sizeKey, b.panelShape) }
    );

  // الطول/العرض اليدوي (أخرى) والعدد
  const updateBoardDims = (key, field, value) =>
    patchBoard(key, (b) => ({ ...b, [field]: digitsOnly(value) }));

  // خيار الإطار ونوعه ولونه - تغيير الخيار/النوع يمسح اللي بعده لأن القوائم والأسعار تختلف
  const updateBoardFrame = (key, field, value) =>
    patchBoard(key, (b) => {
      if (field === "frameOption") return { ...b, frameOption: value, frameType: "", frameColor: "", customFrameColor: "" };
      if (field === "frameType") return { ...b, frameType: value, frameColor: "", customFrameColor: "" };
      if (field === "frameColor") return { ...b, frameColor: value, customFrameColor: "" };
      return { ...b, [field]: value };
    });

  const updateBoardPriceOption = (key, optionId) =>
    patchBoard(key, (b) => ({ ...b, priceOptionId: optionId }));

  // إذا وصل سعر المتر المعتمد من السيرفر بعد ما بدأ يكتب، نعيد الحساب
  useEffect(() => {
    setBoards((prev) => prev.map((b) => (b.priceOptionId ? withAutoPrice(b) : b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitPrice]);

  const updateBoardPrice = (key, value) => {
    setBoards((prev) =>
      prev.map((b) =>
        b.key === key ? { ...b, price: formatAmount(digitsOnly(value)) } : b
      )
    );
  };

  const addBoardImages = (key, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const images = files.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setBoards((prev) =>
      prev.map((b) => (b.key === key ? { ...b, images: [...b.images, ...images] } : b))
    );
  };

  const removeBoardImage = (key, index) => {
    setBoards((prev) =>
      prev.map((b) => {
        if (b.key !== key) return b;
        URL.revokeObjectURL(b.images[index].previewUrl);
        return { ...b, images: b.images.filter((_, i) => i !== index) };
      })
    );
  };

  const addBoard = () => setBoards((prev) => [...prev, newBoard()]);

  const removeBoard = (key) => {
    setBoards((prev) => {
      const target = prev.find((b) => b.key === key);
      target?.images.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return prev.filter((b) => b.key !== key);
    });
  };

  const subtotal = boards.reduce((sum, b) => sum + parseAmount(b.price), 0);
  const discountPct = Math.min(Number(discountPercent) || 0, 99);
  const discountAmount = Math.round(subtotal * (discountPct / 100));
  const finalTotal = subtotal - discountAmount;

  const validate = () => {
    if (!customer.customerName || !customer.customerPhone) {
      return "يرجى إدخال اسم الزبون ورقم الهاتف.";
    }
    if (!customer.governorate) return "يرجى اختيار المحافظة.";
    if (!customer.nearestPoint.trim()) return "يرجى إدخال أقرب نقطة دالة.";
    if (!boards.length) return "يرجى إضافة لوحة واحدة على الأقل.";
    for (let i = 0; i < boards.length; i++) {
      const b = boards[i];
      const title = boardTitle(i);
      if (!b.panelShape) return `يرجى اختيار شكل اللوحة لـ${title}.`;
      if (!b.sizeKey) return `يرجى اختيار القياس لـ${title}.`;
      if (!(Number(b.lengthCm) > 0) || !(Number(b.widthCm) > 0)) {
        return `يرجى إدخال الطول والعرض لـ${title}.`;
      }
      if (!(Number(b.quantity) >= 1)) return `يرجى إدخال العدد لـ${title}.`;
      if (!b.frameOption) return `يرجى اختيار الإطار لـ${title}.`;
      if (b.frameOption === "full") {
        if (!b.frameType) return `يرجى اختيار نوع الإطار (مودرن/كلاسك) لـ${title}.`;
        if (isOrderMode && !b.frameColor) return `يرجى اختيار لون الإطار لـ${title}.`;
        if (isOrderMode && b.frameColor === OTHER && !b.customFrameColor.trim()) {
          return `يرجى كتابة لون الإطار لـ${title}.`;
        }
      }
      if (!b.priceOptionId) return `يرجى اختيار نوع اللوحة (Origin/Edition) لـ${title}.`;
      if (!(parseAmount(b.price) > 0)) return `يرجى إدخال المبلغ لـ${title}.`;
      if (!b.images.length) return `يرجى رفع صورة واحدة على الأقل لـ${title}.`;
    }
    if (finalTotal <= 0) return "يجب أن يكون المجموع النهائي أكبر من صفر.";
    return "";
  };

  // 📄 بيانات فاتورة من المعلومات المدخلة (قبل الحفظ) - نفس بناء الفاتورة
  // بالسيرفر (partnerBoardToInvoiceItem + invoiceData بـinvoiceController.js)
  // حتى ملف الـPDF يطلع بنفس تصميم فواتيرنا. رقم الفاتورة ما يتحدد إلا بعد
  // الحفظ، فيظهر "مسودة". صور اللوحات من روابط المعاينة المحلية.
  const buildInvoiceDraft = () => {
    const items = boards.map((b, index) => {
      const inner = b.frameOption === "inner";
      const quantity = Number(b.quantity);
      const price = parseAmount(b.price);
      const isOtherColor = isOrderMode && b.frameColor === OTHER;
      return {
        manualPrice: true,
        panelName: `لوحة ${index + 1}`,
        panelType: INVOICE_PANEL_TYPES[b.priceOptionId],
        panelShape: b.panelShape,
        frameOption: inner ? "إطار داخلي فقط" : "إطار كامل",
        frameType: inner ? "" : b.frameType,
        customFrameType: "",
        sizeSource: "custom",
        sizeKey: `${b.widthCm}x${b.lengthCm}`,
        customHeight: String(b.lengthCm),
        customWidth: String(b.widthCm),
        frameColor: inner || !isOrderMode ? "" : b.frameColor,
        customFrameColor: isOtherColor ? b.customFrameColor.trim() : "",
        quantity,
        pricePerPanel: Math.round(price / quantity),
        total: price,
        panelImages: b.images.map((img, i) => ({ id: `draft-${index}-${i}`, preview: img.previewUrl, url: img.previewUrl })),
      };
    });
    return {
      invoiceNumber: "مسودة",
      customerName: customer.customerName.trim(),
      phone: customer.customerPhone,
      projectName: "",
      companyName: "",
      governorate: customer.governorate,
      nearestPoint: customer.nearestPoint.trim(),
      date: new Date().toISOString().substring(0, 10),
      items,
      subtotal,
      consultationFee: 0,
      installationFee: 0,
      installationFeeManual: 0,
      transportFee: 0,
      transportFeeManual: 0,
      siteVisitFee: 0,
      hideTransportFeeFromCustomer: false,
      // الخصم ظاهر بالمجاميع، فما ينكتب بالملاحظات (نفس الفاتورة بالسيرفر)
      notes: ["طلب من ارت تاتش", notes.trim()].filter(Boolean).join("\n"),
      discount: discountAmount,
      finalTotal,
      paymentMethod: "cod",
      advancePayment: 0,
      remainingPayment: finalTotal,
      saleAccount: "بيع",
    };
  };

  const handleCreatePartnerOrder = async () => {
    setSuccessMessage("");
    setErrorMessage("");

    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setSavingOrder(true);

    // 1) ملف PDF الفاتورة ينزل على جهاز المستخدم أولاً (بدون رفع)، بعدها
    // يكمل الحفظ الطبيعي. إذا فشل إنشاء الملف يختار يكمل الحفظ أو يوقف
    setSavingStep("pdf");
    let pdfDownloaded = false;
    try {
      await downloadInvoicePdf(buildInvoiceDraft(), safePdfName(customer.customerName));
      pdfDownloaded = true;
    } catch (pdfErr) {
      console.error("Failed to build partner invoice PDF:", pdfErr);
      if (!window.confirm("تعذّر إنشاء ملف الفاتورة (PDF). هل تريد متابعة حفظ الطلب بدونه؟")) {
        setSavingOrder(false);
        setSavingStep("");
        return;
      }
    }
    setSavingStep("save");

    try {

      const boardsPayload = [];
      // كل صور كل اللوحات تنرفع سوا (بدل وحدة بعد الثانية) وبنفس الترتيب -
      // الرقم بالمسار حتى ما يتطابق اسمين بنفس اللحظة
      const stamp = Date.now();
      const uploadedByBoard = await Promise.all(
        boards.map((b, bi) =>
          Promise.all(
            b.images.map(async ({ file }, ii) => {
              const imgRef = ref(
                storage,
                `partners/${PARTNER_ID}/salesImages/${stamp}_${bi}_${ii}_${file.name}`
              );
              await uploadBytes(imgRef, file);
              return getDownloadURL(imgRef);
            })
          )
        )
      );

      for (let bi = 0; bi < boards.length; bi++) {
        const b = boards[bi];
        const imageUrls = uploadedByBoard[bi];
        const option = priceOptionsFor(b).find((o) => o.id === b.priceOptionId);
        const withOuterFrame = b.frameOption === "full";
        boardsPayload.push({
          lengthCm: Number(b.lengthCm),
          widthCm: Number(b.widthCm),
          quantity: Number(b.quantity),
          priceType: option ? option.label : "",
          price: parseAmount(b.price),
          imageUrls,
          panelShape: b.panelShape,
          frameOption: b.frameOption,
          ...(withOuterFrame && { frameType: b.frameType }),
          panelTier: b.priceOptionId,
          ...(isOrderMode && withOuterFrame && {
            frameColor: b.frameColor,
            ...(b.frameColor === OTHER && { customFrameColor: b.customFrameColor.trim() }),
          }),
        });
      }

      // بيع مباشر: status/source/partnerId/employee/createdAt تُحسَب بالسيرفر
      // (Phase 05E). طلب جديد: فاتورة غير معتمدة تنحسب مبالغها بالسيرفر.
      // بالحالتين المجموع/الخصم/السعر النهائي ينعاد حسابهم من boards.
      const res = isOrderMode
        ? await apiFetch(`${API_BASE}/invoices/partner`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customerName: customer.customerName,
              customerPhone: customer.customerPhone,
              governorate: customer.governorate,
              nearestPoint: customer.nearestPoint.trim(),
              boards: boardsPayload,
              discountPercent: discountPct,
              notes: notes.trim(),
            }),
          })
        : await apiFetch(`${API_BASE}/orders/partner`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              customerName: customer.customerName,
              customerPhone: customer.customerPhone,
              governorate: customer.governorate,
              nearestPoint: customer.nearestPoint.trim(),
              price: finalTotal,
              orderImageUrls: boardsPayload.flatMap((b) => b.imageUrls),
              boards: boardsPayload,
              discountPercent: discountPct,
              notes: notes.trim(),
            }),
          });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "فشل حفظ الطلب");
      }

      // طلب جديد: نولّد صورة الفاتورة + PDF بنفس شكل فواتير الموظفات ونربطهن
      // بالفاتورة. الفاتورة نفسها انحفظت - لو فشل التوليد ما نلغي الطلب،
      // الموظفة تقدر تولّدهن بحفظ الفاتورة من واجهة التعديل
      let assetsFailed = false;
      if (isOrderMode) {
        try {
          const created = await res.json();
          const { imageUrl, pdfUrl } = await generateInvoiceAssets(
            created.invoiceData,
            `partners/${PARTNER_ID}/invoices`
          );
          const assetsRes = await apiFetch(`${API_BASE}/invoices/partner/${created.id}/assets`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ imageUrl, pdfUrl }),
          });
          if (!assetsRes.ok) throw new Error(await assetsRes.text().catch(() => ""));
        } catch (assetsErr) {
          console.error("Failed to generate partner invoice assets:", assetsErr);
          assetsFailed = true;
        }
      }

      boards.forEach((b) => b.images.forEach((img) => URL.revokeObjectURL(img.previewUrl)));
      setCustomer({ customerName: "", customerPhone: "", governorate: "", nearestPoint: "" });
      setNotes("");
      setBoards([newBoard()]);
      setDiscountPercent("");

      setSuccessMessage(
        (isOrderMode
          ? assetsFailed
            ? "تم إرسال الطلب إلى قسم الفواتير بنجاح. (لم تُنشأ صورة الفاتورة، وستُنشأ عند حفظها من قبل الموظفة)"
            : "تم إرسال الطلب بنجاح، وسيُحال إلى قسم الفواتير للمراجعة والاعتماد."
          : "تم حفظ الطلب بنجاح.") +
          (pdfDownloaded ? " وتم تنزيل نسخة الفاتورة (PDF) على جهازك." : "")
      );
    } catch (e) {
      setErrorMessage(
        "حدث خطأ أثناء حفظ الطلب، يرجى المحاولة مرة أخرى." +
          (pdfDownloaded ? " (ملف الـPDF المنزَّل لا يعني أن الطلب محفوظ)" : "")
      );
    } finally {
      setSavingOrder(false);
      setSavingStep("");
    }
  };

  // ===== عرض =====
  const alertRef = useRef(null);
  useEffect(() => {
    if ((errorMessage || successMessage) && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [errorMessage, successMessage]);

  const placeholder = (text) => (v) => v || <span style={{ color: P.faint }}>{text}</span>;

  return (
    <PartnerShell maxWidth={760}>
      <PageHeader title="إضافة طلب" subtitle="يرجى إدخال بيانات الطلب كاملةً، ثم الضغط على زر «حفظ الطلب». سيتم تنزيل نسخة من الفاتورة بصيغة PDF على جهازك قبل الحفظ." />

      {/* نوع العملية */}
      <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <SectionTitle>نوع الطلب</SectionTitle>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel
            value="order"
            control={<Radio />}
            label={<RadioText title="طلب جديد" hint="يُحال الطلب إلى قسم الفواتير للمراجعة والاعتماد قبل التنفيذ." />}
          />
          <FormControlLabel
            value="direct"
            control={<Radio />}
            label={<RadioText title="بيع مباشر" hint="يُسجَّل الطلب مباشرةً ضمن مبيعات ارت تاتش." />}
          />
        </RadioGroup>
      </Card>

      {/* بيانات الزبون */}
      <Card sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
        <SectionTitle>بيانات الزبون</SectionTitle>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Field
            label="اسم الزبون"
            value={customer.customerName}
            onChange={(e) => setCustomer((p) => ({ ...p, customerName: e.target.value }))}
          />
          <Field
            label="رقم هاتف الزبون"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", style: { direction: "ltr", textAlign: "right" } }}
            value={customer.customerPhone}
            onChange={(e) =>
              setCustomer((p) => ({ ...p, customerPhone: digitsOnly(e.target.value) }))
            }
          />
          <Field
            label="المحافظة"
            select
            sx={{ gridColumn: { sm: "1 / -1" } }}
            value={customer.governorate}
            onChange={(e) => setCustomer((p) => ({ ...p, governorate: e.target.value }))}
            SelectProps={{ displayEmpty: true, renderValue: placeholder("اختر المحافظة"), MenuProps: { PaperProps: { sx: { maxHeight: 340 } } } }}
          >
            {IRAQ_GOVERNORATES.map((g) => (
              <MenuItem key={g} value={g} sx={{ justifyContent: "flex-end" }}>
                {g}
              </MenuItem>
            ))}
          </Field>
          <Field
            label="أقرب نقطة دالة"
            sx={{ gridColumn: { sm: "1 / -1" } }}
            value={customer.nearestPoint}
            onChange={(e) => setCustomer((p) => ({ ...p, nearestPoint: e.target.value }))}
          />
          <Field
            label="ملاحظات"
            optional
            multiline
            minRows={2}
            sx={{ gridColumn: { sm: "1 / -1" } }}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Box>
      </Card>

      {/* اللوحات */}
      {boards.map((b, index) => {
        const options = priceOptionsFor(b);
        return (
          <Card key={b.key} sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <SectionTitle sx={{ mb: 0 }}>{boardTitle(index)}</SectionTitle>
              {boards.length > 1 && (
                <Button size="small" color="error" onClick={() => removeBoard(b.key)}>
                  حذف اللوحة
                </Button>
              )}
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Field
                label="شكل اللوحة"
                select
                value={b.panelShape}
                onChange={(e) => updateBoardShape(b.key, e.target.value)}
                SelectProps={{ displayEmpty: true, renderValue: placeholder("اختر شكل اللوحة") }}
              >
                {PANEL_SHAPES.map((s) => (
                  <MenuItem key={s} value={s} sx={{ justifyContent: "flex-end" }}>{s}</MenuItem>
                ))}
              </Field>

              <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 1.5 }}>
                <Field
                  label="القياس"
                  select
                  value={b.sizeKey}
                  onChange={(e) => updateBoardSize(b.key, e.target.value)}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) =>
                      v === CUSTOM_SIZE ? "أخرى (قياس يدوي)" : v ? `${v.replace("x", " × ")} سم` : <span style={{ color: P.faint }}>اختر القياس</span>,
                    MenuProps: { PaperProps: { sx: { maxHeight: 340 } } },
                  }}
                >
                  {sizesForShape(b.panelShape).map((s) => (
                    <MenuItem key={s} value={s} sx={{ justifyContent: "flex-end", direction: "ltr" }}>
                      {s.replace("x", " × ")}
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_SIZE} sx={{ justifyContent: "flex-end" }}>أخرى (قياس يدوي)</MenuItem>
                </Field>
                <Field
                  label="العدد"
                  inputProps={{ inputMode: "numeric" }}
                  value={b.quantity}
                  onChange={(e) => updateBoardDims(b.key, "quantity", e.target.value)}
                />
              </Box>

              {b.sizeKey === CUSTOM_SIZE && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                  <Field
                    label="الطول"
                    inputProps={{ inputMode: "numeric" }}
                    InputProps={{ endAdornment: <Suffix>سم</Suffix> }}
                    value={b.lengthCm}
                    onChange={(e) => updateBoardDims(b.key, "lengthCm", e.target.value)}
                  />
                  <Field
                    label="العرض"
                    inputProps={{ inputMode: "numeric" }}
                    InputProps={{ endAdornment: <Suffix>سم</Suffix> }}
                    value={b.widthCm}
                    onChange={(e) => updateBoardDims(b.key, "widthCm", e.target.value)}
                  />
                </Box>
              )}

              {/* الإطار والنوع - يطلعون بعد ما يكتمل القياس */}
              {!hasDims(b) ? (
                <Typography sx={{ p: 1.5, textAlign: "center", fontSize: 14, color: P.muted, background: P.surface2, borderRadius: "10px" }}>
                  يرجى اختيار القياس أولاً لعرض خيارات الإطار ونوع اللوحة.
                </Typography>
              ) : (
                <>
                  <Box>
                    <FieldLabel>الإطار</FieldLabel>
                    <RadioGroup value={b.frameOption} onChange={(e) => updateBoardFrame(b.key, "frameOption", e.target.value)}>
                      {FRAME_OPTION_CHOICES.map((o) => (
                        <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
                      ))}
                    </RadioGroup>
                  </Box>

                  {b.frameOption === "full" && (
                    <Box>
                      <FieldLabel>نوع الإطار</FieldLabel>
                      <RadioGroup row value={b.frameType} onChange={(e) => updateBoardFrame(b.key, "frameType", e.target.value)}>
                        {PARTNER_FRAME_TYPES.map((t) => (
                          <FormControlLabel key={t.value} value={t.value} control={<Radio />} label={t.label} />
                        ))}
                      </RadioGroup>
                    </Box>
                  )}

                  {isOrderMode && b.frameOption === "full" && b.frameType && (
                    <Field
                      label="لون الإطار"
                      select
                      value={b.frameColor}
                      onChange={(e) => updateBoardFrame(b.key, "frameColor", e.target.value)}
                      SelectProps={{ displayEmpty: true, renderValue: (v) => (v ? formatFrameColor(v) : <span style={{ color: P.faint }}>اختر لون الإطار</span>) }}
                    >
                      {(FRAME_COLORS[b.frameType] || []).map((c) => (
                        <MenuItem key={c} value={c} sx={{ justifyContent: "flex-end" }}>
                          {formatFrameColor(c)}
                        </MenuItem>
                      ))}
                      <MenuItem value={OTHER} sx={{ justifyContent: "flex-end" }}>أخرى</MenuItem>
                    </Field>
                  )}

                  {isOrderMode && b.frameOption === "full" && b.frameColor === OTHER && (
                    <Field
                      label="اكتب لون الإطار"
                      value={b.customFrameColor}
                      onChange={(e) => updateBoardFrame(b.key, "customFrameColor", e.target.value)}
                    />
                  )}

                  {b.frameOption && (b.frameOption === "inner" || b.frameType) && (
                    <Box>
                      <FieldLabel>نوع اللوحة</FieldLabel>
                      <RadioGroup row value={b.priceOptionId} onChange={(e) => updateBoardPriceOption(b.key, e.target.value)}>
                        {["origin", "edition"].map((tier) => {
                          const option = options.find((o) => o.id === tier);
                          return (
                            <FormControlLabel
                              key={tier}
                              value={tier}
                              control={<Radio />}
                              label={
                                <span>
                                  <b style={{ direction: "ltr", display: "inline-block" }}>{tier === "origin" ? "Origin" : "Edition"}</b>
                                  {option && <span style={{ color: P.muted }}> - {option.price.toLocaleString()} د.ع</span>}
                                </span>
                              }
                            />
                          );
                        })}
                      </RadioGroup>
                    </Box>
                  )}
                </>
              )}

              <Field
                label="المبلغ"
                inputProps={{ inputMode: "numeric", style: { fontWeight: 700 } }}
                InputProps={{ endAdornment: <Suffix>د.ع</Suffix> }}
                value={b.price}
                onChange={(e) => updateBoardPrice(b.key, e.target.value)}
                helperText="يُحسب تلقائياً (سعر اللوحة × العدد)، ويمكن تعديله عند الحاجة."
              />

              {/* صور اللوحة */}
              <Box>
                <Button
                  component="label"
                  htmlFor={`board-image-${b.key}`}
                  variant="outlined"
                  fullWidth
                  sx={{ borderStyle: "dashed", minHeight: 50 }}
                >
                  📷 رفع صورة اللوحة
                </Button>
                <input
                  id={`board-image-${b.key}`}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => {
                    addBoardImages(b.key, e.target.files);
                    e.target.value = ""; // حتى يكدر يختار نفس الصورة مرة ثانية بعد حذفها
                  }}
                />

                {b.images.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
                    {b.images.map((img, imgIndex) => (
                      <Box
                        key={img.previewUrl}
                        sx={{ position: "relative", width: 88, height: 88, borderRadius: "10px", overflow: "hidden", border: `1px solid ${P.line}` }}
                      >
                        <img
                          src={img.previewUrl}
                          alt={img.file.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => removeBoardImage(b.key, imgIndex)}
                          aria-label="حذف الصورة"
                          sx={{
                            position: "absolute",
                            top: 4,
                            left: 4,
                            width: 24,
                            height: 24,
                            fontSize: 13,
                            color: "#fff",
                            backgroundColor: "rgba(179,38,30,.9)",
                            "&:hover": { backgroundColor: P.danger },
                          }}
                        >
                          ✕
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          </Card>
        );
      })}

      <Button variant="outlined" fullWidth sx={{ mb: 2, minHeight: 50, background: "#fff" }} onClick={addBoard}>
        ➕ إدخال {boardTitle(boards.length)}
      </Button>

      {/* المجاميع */}
      <Card sx={{ p: { xs: 2, md: 2.5 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ color: P.muted, fontWeight: 700 }}>المجموع الأولي</Typography>
          <Money value={subtotal} size={17} />
        </Box>

        <Field
          label="الخصم %"
          optional
          size="small"
          inputProps={{ inputMode: "decimal" }}
          value={discountPercent}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d.]/g, "");
            if (v === "" || (Number(v) >= 0 && Number(v) < 100)) setDiscountPercent(v);
          }}
          helperText={discountAmount > 0 ? `قيمة الخصم: ${discountAmount.toLocaleString()} د.ع` : " "}
        />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5, mb: 2, borderTop: `1px solid ${P.line}` }}>
          <Typography sx={{ fontWeight: 800, fontSize: 17 }}>المجموع النهائي</Typography>
          <Money value={finalTotal} size={24} color={P.success} />
        </Box>

        <Box ref={alertRef}>
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </Box>

        <Button
          variant="contained"
          fullWidth
          disabled={savingOrder}
          onClick={handleCreatePartnerOrder}
          sx={{ minHeight: 52, fontSize: 16 }}
        >
          {savingOrder ? (
            <>
              <CircularProgress size={18} sx={{ color: "inherit", ml: 1 }} />
              {savingStep === "pdf" ? "جارٍ إنشاء ملف الفاتورة (PDF)..." : "جاري الحفظ..."}
            </>
          ) : (
            "حفظ الطلب"
          )}
        </Button>
      </Card>
    </PartnerShell>
  );
};

const SectionTitle = ({ children, sx }) => (
  <Typography sx={{ fontWeight: 800, fontSize: 17, mb: 2, ...sx }}>{children}</Typography>
);

const FieldLabel = ({ children }) => (
  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: P.ink2, mb: 0.25 }}>{children}</Typography>
);

const RadioText = ({ title, hint }) => (
  <Box sx={{ py: 0.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
    <Typography sx={{ fontSize: 12.5, color: P.muted }}>{hint}</Typography>
  </Box>
);

export default PartnerAddOrder;
